<template>
  <BaseDossierPageLayout
    :title="$t('global.financing')"
    with-next-button
    :dossier-data-changed="dossierDataChanged"
    :submit-method="submitForm"
    @canceled="() => handleCancel(dossier.id)"
  >
    <template #cta>
      <BaseButton
        v-if="editionMode"
        :title="$t('button.add_new_m', { var: $t('global.financing') })"
        theme-color="bg-primary text-contrast-primary"
        icon="plus"
        @click="isPaymentFormModalOpen = true"
      />
    </template>
    <template #default>
      <FormValidationObserver
        root-id="dossier_layout_container"
        :margin-top="63"
        :margin-bottom="63"
      >
        <div v-if="dossier?.properties" class="flex flex-1 flex-col gap-4 p-4">
          <SectionWrapper
            v-if="isFormEditorMode || !isSectionHidden('insurance')"
            section-key="insurance"
            :can-manage="$can('manage_settings')"
            :is-hidden="isSectionHidden('insurance')"
          >
            <div class="flex-1">
              <PageSection :title="$t('global.insurance')" disable-sticky-margin>
                <template #default>
                  <div class="flex flex-col divide-y divide-slate-200">
                    <div class="grid-form empty:hidden">
                      <div>
                        <BaseShowEditInput
                          v-model="dossier.properties.insurer"
                          :label="$t('attributes.insurer')"
                          :edition-mode="editionMode"
                        />
                      </div>
                      <div>
                        <BaseShowEditInput
                          v-model="dossier.properties.insurance_policy_number"
                          :label="$t('attributes.policy_number')"
                          :edition-mode="editionMode"
                        />
                      </div>
                      <div>
                        <BaseShowEditInput
                          v-model="dossier.properties.insurance_capital"
                          :label="$t('attributes.capital')"
                          :edition-mode="editionMode"
                        />
                      </div>
                      <div>
                        <BaseShowEditInput
                          v-model="dossier.properties.insurance_contact"
                          :label="$t('global.contact')"
                          :edition-mode="editionMode"
                        />
                      </div>
                      <div class="md:col-span-2">
                        <BaseShowEditTextArea
                          v-model="dossier.properties.insurance_infos"
                          :label="$t('attributes.informations')"
                          :edition-mode="editionMode"
                          name="insurance_infos"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </PageSection>
            </div>
          </SectionWrapper>
          <SectionWrapper
            v-if="isFormEditorMode || !isSectionHidden('payments')"
            section-key="payments"
            :can-manage="$can('manage_settings')"
            :is-hidden="isSectionHidden('payments')"
          >
            <div class="flex-1">
              <PageSection :title="$t('global.financing')" disable-sticky-margin>
                <template #default>
                  <template v-if="dossier.payments?.length > 0">
                    <div class="flex min-h-0 flex-1 flex-col gap-4">
                      <div class="flex-1 overflow-y-auto">
                        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
                          <PaymentDossierCard
                            v-for="payment in dossier.payments"
                            :key="payment.id"
                            :payment="payment"
                            :payment-modes="[]"
                            @delete="deletePayment"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                  <div
                    v-else
                    class="flex flex-1 flex-row items-center justify-center text-slate-300"
                  >
                    {{
                      $t('global.there_is_no_yet', {
                        settings: $t('global.financing')
                      })
                    }}
                  </div>
                </template>
              </PageSection>
            </div>
          </SectionWrapper>
          <HiddenSectionChips :hidden-sections="hiddenPageSections" />
        </div>
      </FormValidationObserver>
    </template>
  </BaseDossierPageLayout>
  <ConfirmModal
    ref="confirmDossierInformationPage"
    close-popup-button
    only-close-popup
  />
  <PaymentDossierFormModal
    v-model:is-modal-open="isPaymentFormModalOpen"
    :dossier="dossier"
  />
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import BaseDossierPageMixin from '@u/mixins/Dossier/BaseDossierPageMixin'
import SectionManagerMixin from '@/mixins/sectionManagerMixin'
import BaseDossierPageLayout from '@c/addf-package/components/layout/BaseDossierPageLayout.vue'
import FormValidationObserver from '@c/FormValidationObserver.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import PaymentDossierFormModal from '@/views/dossier/form/subpages/financing/component/PaymentDossierFormModal.vue'
import PaymentDossierCard from '@/views/dossier/form/subpages/financing/component/PaymentDossierCard.vue'
import PageSection from '@c/PageSection.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import SectionWrapper from '@c/dossier/sections/SectionWrapper.vue'
import HiddenSectionChips from '@c/dossier/sections/HiddenSectionChips.vue'

export default {
  name: 'TheDossierFinancingPage',
  components: {
    BaseShowEditInput,
    BaseShowEditTextArea,
    PageSection,
    PaymentDossierCard,
    PaymentDossierFormModal,
    BaseButton,
    ConfirmModal,
    FormValidationObserver,
    BaseDossierPageLayout,
    SectionWrapper,
    HiddenSectionChips,
  },
  mixins: [BaseDossierPageMixin, SectionManagerMixin],
  sectionPageKey: 'financing',
  sectionDefs: [
    { key: 'insurance', label: 'global.insurance' },
    { key: 'payments', label: 'global.financing' },
  ],
  async beforeRouteLeave(to, from, next) {
    if (to.name === 'dashboard_dossier_page_route') {
      next()
    } else {
      if (this.editionMode && this.dossierDataChanged) {
        let confirm = await this.$refs.confirmDossierInformationPage.show({
          title: this.$t('global.there_is_unsaved_changes'),
          okButton: this.$t('button.save_and_continue'),
          cancelButton: this.$t('button.leave_anyway')
        })
        if (confirm) {
          await this.submitForm()
          next()
        } else {
          await this.processFetchDossierByIdAction(this.dossier.id)
          next()
        }
      } else {
        next()
      }
    }
  },
  props: {
    dossierDataChanged: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:dossierDataChanged'],
  data() {
    return {
      reload: false,
      isPaymentFormModalOpen: false
    }
  },
  computed: {
    localDossierDataChanged: {
      get() {
        return this.dossierDataChanged
      },
      set(value) {
        this.$emit('update:dossierDataChanged', value)
      }
    }
  },
  watch: {
    reload: {
      handler(newVal) {
        if (newVal === true) {
          this.dossier = this.processFetchDossierByIdAction(this.dossier.id)
          this.reload = false
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.processSetNavSubItemsMutation()
  },
  methods: {
    ...mapActions({
      processUnlinkPaymentAction: 'dossier/unlinkPayment',
      processUpdateDossierFinancingAction: 'dossier/updateDossierFinancing'
    }),
    ...mapMutations({
      processSetNavSubItemsMutation: 'dossier/setNavSubItems'
    }),
    submitForm() {
      this.dossier['light_form'] = false

      return this.processUpdateDossierFinancingAction({
        dossier_id: this.$route.params.id,
        dossier: this.dossier
      }).then((response) => {
        this.localDossierDataChanged = false
        this.$h.toastSuccess(this.$t('toasts.dossier_particularities_update'))
      })
    },
    deletePayment(payment_id, deletion_type) {
      this.processUnlinkPaymentAction({
        payment_id: payment_id,
        dossier_id: this.dossier.id,
        deletion_type: deletion_type
      }).then((response) => {
        this.$h.toastSuccess('un payment a été supprimé')
      })
    },
    handleCancel(id) {
      this.processFetchDossierByIdAction(id)
    }
  }
}
</script>
